import type { EditionsGalleryItemsParams } from '#lib/websites/editions/api/getEditionsGalleryPage.js';
import { SERIES_GALLERY_LIMIT } from '#lib/websites/editions/constants/gallery.js';
import { SeriesGallerySort } from '#lib/websites/editions/constants/sorting.js';
import type { FilterSections } from '$global/features/filter-drawer/types';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';

type Params = NonNullable<EditionsGalleryItemsParams>;
type Sort = Params['sort'];

const SORTS: Sort[] = [
	SeriesGallerySort.NumberAsc,
	SeriesGallerySort.NumberDsc,
] as Sort[];

type SpinsLeaf = Record<string, unknown> & {
	canvas?: { _in: string[] };
	blur?: { _eq: boolean };
	num_words_in_title?: { _gte?: number; _lte?: number };
	style?: { name: { _in: string[] } };
};

// The drawer builds these from the `Filters` record on `Series.filter` plus
// getSeriesFilterSections' synthetic sub-series tag list. On dev that record
// is the H12 family's — three tag lists and one slider, all reaching through
// `Editions.attributes` into `Attributes_Spins`. A section whose field_name is
// not one of these is ignored rather than guessed at.
interface AppliedSeriesFilter {
	series?: { code: { _in: string[] } };
	attributes?: { item__Attributes_Spins?: SpinsLeaf };
	_and?: AppliedSeriesFilter[];
}

export const getSeriesBaseParams = (slug: string): Params => ({
	scope: 'series',
	seriesSlug: slug,
	sort: SeriesGallerySort.NumberAsc as Sort,
	limit: SERIES_GALLERY_LIMIT,
	offset: 0,
});

const applySpinsLeaf = (params: Params, leaf: SpinsLeaf | undefined) => {
	if (!leaf) return;

	if (leaf.canvas) {
		params.canvas = leaf.canvas._in;
	}
	if (leaf.style) {
		params.spinStyles = leaf.style.name._in;
	}
	if (leaf.blur) {
		params.blur = String(leaf.blur._eq) as 'true' | 'false';
	}
	if (leaf.num_words_in_title?._gte !== undefined) {
		params.wordsMin = leaf.num_words_in_title._gte;
	}
	if (leaf.num_words_in_title?._lte !== undefined) {
		params.wordsMax = leaf.num_words_in_title._lte;
	}
};

const applyEntry = (params: Params, entry: AppliedSeriesFilter) => {
	if (entry.series) {
		params.seriesCodes = entry.series.code._in;
	}

	applySpinsLeaf(params, entry.attributes?.item__Attributes_Spins);

	// A slider arrives as `_and: [{ _gte }, { _lte }]` — two terms wrapped
	// together rather than one leaf.
	entry._and?.forEach((nested) => applyEntry(params, nested));
};

export const getSeriesRestParams = ({
	slug,
	searchParams,
	filterSections,
}: {
	slug: string;
	searchParams: URLSearchParams;
	filterSections: FilterSections;
}): Params => {
	const applied = getAppliedFilters({
		searchParams,
		filterSections,
	}) as AppliedSeriesFilter[];

	const sort = getAppliedSort({
		searchParams,
		defaultSort: [SeriesGallerySort.NumberAsc],
	})[0] as Sort;

	const params: Params = {
		...getSeriesBaseParams(slug),
		sort: SORTS.includes(sort) ? sort : (SeriesGallerySort.NumberAsc as Sort),
	};

	applied.forEach((entry) => applyEntry(params, entry));

	return params;
};
